import { useEffect } from "react";
import { useParams, useNavigate, Navigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calculator, MapPin, Phone } from "lucide-react";
import { locations } from "@/data/locations";
import { Marquee } from "@/components/Marquee";
import { Footer } from "@/components/Footer";

const RevealLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function LocationPage() {
  const { city } = useParams();
  const navigate = useNavigate();
  const loc = locations[city];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [city]);

  if (!loc) return <Navigate to="/" replace />;

  const goQuote = () => navigate("/", { state: { scrollTo: "calculator" } });
  const goBook = () => navigate("/", { state: { openBooking: true } });

  return (
    <div data-testid={`location-page-${city}`}>
      <header className="absolute top-0 left-0 right-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="location-back-home" className="flex items-center gap-2 font-mono text-[11px] tracking-[0.25em] uppercase text-sand/70 hover:text-sun transition-colors">
            <ArrowLeft size={14} /> Safe Point
          </Link>
          <div className="flex items-center gap-3">
            <a href="tel:3059868602" className="hidden md:flex items-center gap-2 font-mono text-xs tracking-wider text-sand/80">
              <Phone size={14} className="text-sun" /> (305) 986-8602
            </a>
            <button
              data-testid={`location-book-cta-${city}`}
              onClick={goBook}
              className="bg-sun text-obsidian font-semibold text-sm px-5 py-2.5 rounded-full transition-transform duration-300 hover:scale-[1.04] active:scale-95"
            >
              Book Inspection
            </button>
          </div>
        </div>
      </header>

      <section className="relative bg-obsidian noise overflow-hidden">
        <div className="absolute inset-0 blueprint-grid" />
        <div className="absolute -top-32 -left-32 w-[540px] h-[540px] rounded-full bg-sun/10 blur-[120px]" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-40 pb-24 grid lg:grid-cols-[1.15fr_1fr] gap-16 items-center">
          <div>
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.15, duration: 0.8 }}
              className="inline-flex items-center gap-2.5 border border-sand/15 rounded-full px-4 py-2 mb-8"
            >
              <MapPin size={13} className="text-sun" />
              <span className="font-mono text-[11px] tracking-[0.25em] text-sand/80 uppercase">{loc.tagline}</span>
            </motion.div>

            <h1 className="font-display font-black text-sand text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[0.95]" data-testid={`location-headline-${city}`}>
              {loc.headline.map((l, i) => (
                <RevealLine key={l} delay={0.3 + i * 0.13}>
                  {i === loc.headline.length - 1 ? <span className="text-sun">{l}</span> : l}
                </RevealLine>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.95, duration: 0.8 }}
              className="mt-7 text-sand/60 text-base sm:text-lg max-w-xl leading-relaxed"
            >
              {loc.intro}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.1, duration: 0.8 }}
              className="mt-10 flex flex-wrap gap-4"
            >
              <button
                data-testid={`location-quote-cta-${city}`}
                onClick={goQuote}
                className="group flex items-center gap-3 bg-sun text-obsidian font-semibold px-7 py-4 rounded-full transition-transform duration-300 hover:scale-[1.04] active:scale-95"
              >
                <Calculator size={17} />
                Get a {loc.name} Quote
              </button>
              <button
                data-testid={`location-book-secondary-${city}`}
                onClick={goBook}
                className="group flex items-center gap-3 border border-sand/25 text-sand font-semibold px-7 py-4 rounded-full transition-colors duration-300 hover:border-sun hover:text-sun"
              >
                Book in {loc.name}
                <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94, clipPath: "inset(100% 0 0 0)" }}
            animate={{ opacity: 1, scale: 1, clipPath: "inset(0% 0 0 0)" }}
            transition={{ delay: 0.6, duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
            className="relative hidden lg:block"
          >
            <div className="rounded-2xl overflow-hidden border border-sand/10 shadow-2xl shadow-black/50">
              <img src={loc.img} alt={`${loc.name} Florida home`} className="w-full h-[460px] object-cover" />
              <div className="absolute inset-0 bg-obsidian/15" />
            </div>
            <div className="absolute -bottom-5 -right-4 bg-sand rounded-xl px-5 py-4 shadow-xl">
              <div className="font-mono text-[10px] tracking-[0.25em] text-obsidian/50 uppercase">Serving</div>
              <div className="font-display font-extrabold text-obsidian text-lg leading-tight">{loc.name}, FL</div>
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      <section className="bg-sand py-24 sm:py-32 blueprint-grid-light">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="mb-14">
            <div className="font-mono text-xs tracking-[0.25em] text-terra uppercase font-semibold mb-4">
              / What {loc.name} Homes Need
            </div>
            <h2 className="font-display font-bold tracking-tight text-obsidian text-2xl sm:text-3xl lg:text-4xl max-w-2xl leading-tight">
              Local inspection priorities in {loc.name}.
            </h2>
          </div>

          <div className="grid sm:grid-cols-2 gap-6">
            {loc.needs.map((n, i) => (
              <motion.div
                key={n.title}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: i * 0.1, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                data-testid={`location-need-${city}-${i + 1}`}
                className="group bg-paper border border-sandline rounded-2xl p-8 transition-shadow duration-500 hover:shadow-xl hover:shadow-obsidian/5"
              >
                <div className="w-12 h-12 rounded-xl bg-obsidian flex items-center justify-center mb-6 transition-colors duration-300 group-hover:bg-terra">
                  <n.icon size={20} className="text-sun" />
                </div>
                <h3 className="font-display font-semibold text-xl tracking-tight text-obsidian">{n.title}</h3>
                <p className="mt-3 text-slate-600 text-sm sm:text-base leading-relaxed">{n.text}</p>
              </motion.div>
            ))}
          </div>

          <div className="mt-16 grid grid-cols-3 gap-4">
            {loc.stats.map((s) => (
              <div key={s.label} className="bg-obsidian rounded-2xl px-4 py-7 sm:px-8 text-center" data-testid={`location-stat-${city}-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="font-display font-black text-sun text-2xl sm:text-4xl tracking-tight">{s.value}</div>
                <div className="font-mono text-[9px] sm:text-[10px] tracking-[0.2em] text-sand/50 uppercase mt-2">{s.label}</div>
              </div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-16 bg-terra rounded-2xl px-8 py-12 sm:px-14 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8"
          >
            <div>
              <h3 className="font-display font-bold text-sand text-2xl sm:text-3xl tracking-tight leading-tight">
                Buying or insuring in {loc.name}? Price it in 30 seconds.
              </h3>
              <p className="mt-3 text-sand/70 text-sm sm:text-base max-w-lg">
                The same estimator, tuned for {loc.name} homes — square footage, age, and the
                services local carriers ask for.
              </p>
            </div>
            <button
              data-testid={`location-calculator-cta-${city}`}
              onClick={goQuote}
              className="group shrink-0 flex items-center gap-3 bg-sand text-obsidian font-semibold px-7 py-4 rounded-full transition-transform duration-300 hover:scale-[1.04] active:scale-95"
            >
              <Calculator size={17} />
              Open the Estimator
            </button>
          </motion.div>
        </div>
      </section>

      <Footer onBook={goBook} />
    </div>
  );
}
